import {
  Button,
  Circle,
  CloseButton,
  Drawer,
  Field,
  Float,
  IconButton,
  Input,
  Portal,
  Select,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdFilterAlt } from 'react-icons/md';
import { RiResetLeftFill } from 'react-icons/ri';

import SelectCatalogs from '@/components/common/SelectCatalogs';
import SelectSdgs from '@/components/common/SelectSdgs';
import SelectUnits from '@/components/common/SelectUnits';
import { Route } from '@/routes/_layout/$slug/library.tsx';

import { category } from './LibraryData';

const LibraryFilterDrawer = () => {
  const { slug } = Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });
  const { filter, type } = Route.useSearch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const defaultFilters = {
    pillar_suggestion_name: '',
    commitment_suggestion_name: '',
    name: '',
    lt_indicator_category: [],
    description: '',
    providers: '',
    aids: '',
    lt_indicator_other_unit_in: [],
    sdgs_in: [],
    catalogs_in: [],
  };
  const [filters, setFilters] = useState(() => ({ ...defaultFilters }));
  const { numberOfFilterActive } = useMemo(() => {
    const isActive = (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null;
    };

    const activeCount = Object.values(filters).filter(isActive).length;

    return { numberOfFilterActive: activeCount };
  }, [filters]);
  const [resetTrigger, setResetTrigger] = useState(false);

  useEffect(() => {
    if (filter) {
      const parsedFilters: { [key: string]: any } = {};
      const filterParams = new URLSearchParams(filter);

      filterParams.forEach((value, key) => {
        if (
          key === 'lt_indicator_other_unit_in' ||
          key === 'sdgs_in' ||
          key === 'lt_indicator_category' ||
          key === 'catalogs_in'
        ) {
          parsedFilters[key] = value.split(',');
        } else if (key in filters) {
          parsedFilters[key] = value;
        }
      });

      setFilters((prev) => ({
        ...prev,
        ...parsedFilters,
      }));
    }
  }, [filter]);

  useEffect(() => {
    if (resetTrigger) {
      applyFilters();
      setResetTrigger(false);
    }
  }, [resetTrigger]);

  const handleFilterChange = (column: string, value: any) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const applyFilters = () => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters)
        .filter(
          ([_, value]) =>
            value !== '' && !(Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === '')))
        )
        .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : String(value)])
    );

    const encodedFilter = new URLSearchParams(cleanedFilters).toString();
    navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        filter: encodedFilter.replace(/\+/g, '%20'),
      }),
    });
    setIsDrawerOpen(false);
  };

  const resetFilters = () => {
    setFilters({ ...defaultFilters });
    setResetTrigger(true);
  };

  useEffect(() => {
    if (isDrawerOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          applyFilters();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isDrawerOpen, filters]);

  return (
    <Drawer.Root
      open={isDrawerOpen}
      onOpenChange={(e) => setIsDrawerOpen(e.open)}
      size="sm"
      placement="end"
      onEscapeKeyDown={() => applyFilters()}
    >
      <Drawer.Backdrop />
      <Drawer.Trigger asChild>
        <IconButton variant={'ghost'}>
          <MdFilterAlt />
          {numberOfFilterActive !== 0 && (
            <Float offset="1">
              <Circle size="4" bg="primary" color="white" fontSize={'xs'}>
                {numberOfFilterActive}
              </Circle>
            </Float>
          )}
        </IconButton>
      </Drawer.Trigger>
      <Drawer.Content
        rounded="md"
        position={'fixed'}
        margin={'{spacing.4}'}
        h={'calc(100% - {spacing.4} * 2)'}
        right={0}
        top={0}
      >
        <Drawer.CloseTrigger asChild>
          <CloseButton size="sm" mt="2" mr="4" />
        </Drawer.CloseTrigger>
        <Drawer.Header>
          <Text textStyle={'xl'} fontWeight={'bold'}>
            {t('ui.filter')}
          </Text>
        </Drawer.Header>
        <Drawer.Body>
          {/* Name Filter */}
          <Field.Root>
            <Field.Label>{t('suggestion.filter.indicatorName.contains')}</Field.Label>
            <Input
              placeholder={t('ui.search')}
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
            />
          </Field.Root>

          {/* Description Filter */}
          <Field.Root mt={2}>
            <Field.Label>{t('suggestion.filter.description.contains')}</Field.Label>
            <Input
              placeholder={t('ui.search')}
              value={filters.description}
              onChange={(e) => handleFilterChange('description', e.target.value)}
            />
          </Field.Root>

          {/* Pillar Name Filter */}
          <Field.Root mt={2}>
            <Field.Label>{t('suggestion.filter.pillarName.contains')}</Field.Label>
            <Input
              placeholder={t('ui.search')}
              value={filters.pillar_suggestion_name}
              onChange={(e) => handleFilterChange('pillar_suggestion_name', e.target.value)}
            />
          </Field.Root>

          {/* Commitment Name Filter */}
          <Field.Root mt={2}>
            <Field.Label>{t('suggestion.filter.commitmentName.contains')}</Field.Label>
            <Input
              placeholder={t('ui.search')}
              value={filters.commitment_suggestion_name}
              onChange={(e) => handleFilterChange('commitment_suggestion_name', e.target.value)}
            />
          </Field.Root>

          {/* Providers Filter */}
          {type === 'actions' && (
            <Field.Root mt={2}>
              <Field.Label>{t('suggestion.filter.providers.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.providers}
                onChange={(e) => handleFilterChange('providers', e.target.value)}
              />
            </Field.Root>
          )}

          {/* Aids Filter */}
          {type === 'actions' && (
            <Field.Root mt={2}>
              <Field.Label>{t('suggestion.filter.aids.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.aids}
                onChange={(e) => handleFilterChange('aids', e.target.value)}
              />
            </Field.Root>
          )}

          {/* Category Filter */}
          {type === 'indicators' && (
            <Field.Root mt={2}>
              <Select.Root
                collection={category}
                size="sm"
                value={filters.lt_indicator_category}
                onValueChange={(e) => handleFilterChange('lt_indicator_category', e.value)}
              >
                <Select.HiddenSelect />
                <Select.Label>{t('suggestion.filter.category.select')}</Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('suggestion.filter.category.select')} />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {category.items.map((el) => (
                        <Select.Item item={el} key={el.value}>
                          {el.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Field.Root>
          )}

          {/* Unit Filter */}
          {type === 'indicators' && (
            <Field.Root mt={2}>
              <Field.Label>{t('suggestion.filter.unit.contains')}</Field.Label>
              <SelectUnits
                slug={slug}
                value={filters.lt_indicator_other_unit_in}
                multiple={true}
                type="suggestion"
                onChange={(value) => handleFilterChange('lt_indicator_other_unit_in', value)}
              />
            </Field.Root>
          )}

          {/* Catalogs Filter */}
          <Field.Root mt={2}>
            <Field.Label>{t('suggestion.filter.catalogs.select')}</Field.Label>
            <SelectCatalogs
              value={filters.catalogs_in}
              onChange={(value) =>
                handleFilterChange(
                  'catalogs_in',
                  value.map((catalog) => catalog.id)
                )
              }
            />
          </Field.Root>

          {/* SDGs Filter */}
          <Field.Root mt={2}>
            <Field.Label>{t('suggestion.filter.sdgs.select')}</Field.Label>
            <SelectSdgs
              value={filters.sdgs_in}
              onChange={(value) =>
                handleFilterChange(
                  'sdgs_in',
                  value.map((sdg) => sdg.code)
                )
              }
            />
          </Field.Root>
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="outline" mr={'auto'} onClick={resetFilters}>
            <RiResetLeftFill />
            {t('ui.reset')}
          </Button>
          <Button variant="outline" mr={3} onClick={() => setIsDrawerOpen(false)}>
            {t('ui.cancel')}
          </Button>
          <Button colorScheme="blue" onClick={applyFilters}>
            {t('ui.apply')}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer.Root>
  );
};

export default LibraryFilterDrawer;
